import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertController, LoadingController, ModalController, ToastController} from '@ionic/angular';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {BaseClass} from 'src/app/shared/classes/base';
import {TranslationService} from 'src/app/shared/services/translation.service';
import {AggregatedAreaConfig} from '../360-evaluation/_interfaces/area-config.interface';
import {ProcessConfig} from '../shared/interfaces/process-config.interface';
import {ProcessStatusEnum} from '../shared/interfaces/process.interface';
import {AuthService} from '../shared/services/auth.service';
import {HeadEvaluated} from './_interfaces/head-evaluated.interface';
import {HeadEvaluationService} from './head-evaluation.service';

@Component({
    selector: 'app-head-evaluation',
    templateUrl: './head-evaluation.page.html',
    styleUrls: ['./head-evaluation.page.scss']
})
export class HeadEvaluationPage extends BaseClass implements OnDestroy {
    private _searchSubject: Subject<string> = new Subject();
    public readonly pageSize: number = 3;

    public processConfig: ProcessConfig;
    public userEvaluations: HeadEvaluated[] = [];
    public displayedUserEvaluations: HeadEvaluated[] = [];
    public areas: AggregatedAreaConfig[] = [];
    public status: ProcessStatusEnum;
    public userIndex: number = 0;

    public canReopenOrDeleteEvaluation = false;
    public hasCalibration = false;
    public cycleParticipantId: number;
    public readonly _cycleId: number;
    public allEvaluationsLength: number = 0;


    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _modalController: ModalController,
        private _evaluationService: HeadEvaluationService,
        private _authService: AuthService,
        private _alertController: AlertController,
        private _loadingController: LoadingController
    ) {
        super(_toastController, _translationService);
        this._cycleId = parseInt(
            this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._setSearchSubscription();
    }

    ionViewWillEnter() {
        const userRoles = this._authService.getUserRoles();
        this.canReopenOrDeleteEvaluation = userRoles.includes('Administrator') || userRoles.includes('HR');
        this.getEvaluations(this.userIndex, this.pageSize);
    }
    
    public resetGetEvaluations(): void {
        this.userIndex = 0;
        this.getEvaluations(this.userIndex, this.pageSize);
    }

    public getEvaluations(userIndex: number, pageSize: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this._evaluationService.getHeadEvaluations(
                    this._cycleId, userIndex, pageSize
            ).subscribe(
                    (response) => {
                        this.processConfig = response.data.config;
                        this.status = response.data.status;
                        this.allEvaluationsLength = response.data.allEvaluationsLength;
                        if (this.userIndex > 0) {
                            this.userEvaluations.push(...response.data.headEvaluations);
                            this._cloneEvaluations();
                        } else {
                            this.userEvaluations = response.data.headEvaluations;
                            this._cloneEvaluations();
                        }
                        this.areas = response.data.areas;
                        this.cycleParticipantId = response.data.cycleParticipantId;
                        this.userIndex = this.userEvaluations.length;
                        resolve(); // Resolves the Promise when the data processing is complete
                    },
                    (error) => {
                        this.notifyError(error);
                        reject(error); // Rejects the Promise if an error occurs
                    }
            );
        });
    }
    

    public goBack(): void {
        this._router.navigate(['dashboard']);
    }

    public updateSearch(searchTextValue: string): void {
        this._searchSubject.next(searchTextValue);
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(
            distinctUntilChanged(),
            debounceTime(1000)
        ).subscribe(async (searchValue: string) => {
            if (this.userEvaluations.length < this.allEvaluationsLength)
                await this.getEvaluations(this.userIndex, this.allEvaluationsLength);
            this._setDisplayedUserEvaluations(searchValue);
        });
    }

    private _filterUserAndSubordinates(user: HeadEvaluated, searchValue: string) {
        const userMatches = user.name.toLowerCase().includes(searchValue.toLowerCase());
        
        const filteredSubordinates = user.subordinates
                ? user.subordinates
                        .map(subordinate => this._filterUserAndSubordinates(subordinate, searchValue))
                        .filter(subordinate => subordinate !== null)
                : [];
        
        if (userMatches || filteredSubordinates.length > 0) {
            return { ...user, subordinates: filteredSubordinates };
        }
        
        return null;
    }
    
    private _setDisplayedUserEvaluations(searchValue: string) {
        this.displayedUserEvaluations = this.userEvaluations
                .map(user => this._filterUserAndSubordinates(user, searchValue))
                .filter(user => user !== null);
    }
    
    private _cloneEvaluations()
    {
        this.displayedUserEvaluations = JSON.parse(JSON.stringify(this.userEvaluations));
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }

}
